import React, { Component } from "react";
import { Upload, Table, Tag, Alert, Typography, message } from "antd";
import { UploadOutlined, FileExcelOutlined, DownloadOutlined } from "@ant-design/icons";
import axios from "axios";
import * as XLSX from "xlsx";
import { ModalItem, ButtonItem } from "../../../../../Components/Items";

const { Text } = Typography;

const normalizar = (valor) => String(valor ?? "").trim().toUpperCase();

const parseNumero = (valor) => {
  if (valor === undefined || valor === null || valor === "") return 0;
  if (typeof valor === "number") return valor;
  const limpio = String(valor).replace(/[^0-9.-]/g, "");
  const num = parseFloat(limpio);
  return Number.isNaN(num) ? 0 : num;
};

const ALIAS_CLAVE = ["CLAVE", "CÓDIGO", "CODIGO"];
const ALIAS_DESCRIPCION = ["DESCRIPCION", "DESCRIPCIÓN"];
const ALIAS_CANTIDAD = ["CANTIDAD"];

const obtenerValorColumna = (fila, alias) => {
  const claves = Object.keys(fila);
  for (const texto of alias) {
    const clave = claves.find((k) => normalizar(k).includes(texto));
    if (clave !== undefined) return fila[clave];
  }
  return undefined;
};

class ModalCargarExcelProductosAlCosto extends Component {
  state = {
    cargando: false,
    archivoNombre: null,
    // { clave, descripcionExcel, cantidad, insumo, cantidadProyecto, valido, motivo, aviso }
    filas: [],
  };

  componentDidUpdate(prevProps) {
    if (this.props.visible && !prevProps.visible) {
      this.setState({ cargando: false, archivoNombre: null, filas: [] });
    }
  }

  descargarPlantilla = () => {
    const worksheet = XLSX.utils.json_to_sheet([
      { Clave: "ABC123", Descripcion: "Producto de ejemplo", Cantidad: 10 },
    ]);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Plantilla");
    XLSX.writeFile(workbook, "Plantilla_Productos_Al_Costo.xlsx");
  };

  onSubirArchivo = (info) => {
    const file = info.file;
    if (!/\.xlsx?$/i.test(file.name || "")) {
      message.error("El archivo debe ser un Excel (.xlsx/.xls)");
      return;
    }
    this.setState({ cargando: true, archivoNombre: file.name, filas: [] });

    const reader = new FileReader();
    reader.onloadend = (e) => {
      try {
        const data = new Uint8Array(e.target.result);
        const workbook = XLSX.read(data, { type: "array" });
        const primeraHoja = workbook.Sheets[workbook.SheetNames[0]];
        const filasCrudas = XLSX.utils.sheet_to_json(primeraHoja, { defval: "" });
        this.procesarFilas(filasCrudas);
      } catch (err) {
        console.error(err);
        message.error("No se pudo leer el archivo. Verifica que sea un Excel válido.");
        this.setState({ cargando: false });
      }
    };
    reader.readAsArrayBuffer(file.originFileObj || file);
  };

  procesarFilas = async (filasCrudas) => {
    const filasParseadas = filasCrudas
      .map((fila) => ({
        clave: String(obtenerValorColumna(fila, ALIAS_CLAVE) ?? "").trim(),
        descripcionExcel: String(obtenerValorColumna(fila, ALIAS_DESCRIPCION) ?? "").trim(),
        cantidad: parseNumero(obtenerValorColumna(fila, ALIAS_CANTIDAD)),
      }))
      .filter((fila) => fila.clave);

    if (filasParseadas.length === 0) {
      message.error("No se encontraron filas con una columna de Clave reconocible.");
      this.setState({ cargando: false });
      return;
    }

    const { empresaId, headersToken, proyectoId } = this.props;

    // 1) Todas las claves del Excel en una sola consulta. La descripcion viaja
    // junto con la clave: es lo que desempata cuando dos insumos la comparten.
    let resultados;
    try {
      const res = await axios.post(
        "insumos/resolverClavesInsumosInventariablesMasivo",
        {
          empresaId,
          filas: filasParseadas.map((f) => ({
            clave: f.clave,
            descripcion: f.descripcionExcel,
          })),
        },
        { headers: headersToken }
      );
      if (res.data.success !== 2) {
        message.error(res.data.message || "No se pudieron resolver las claves del Excel");
        this.setState({ cargando: false });
        return;
      }
      resultados = res.data.resultados || [];
    } catch (err) {
      console.error(err);
      message.error("Error de conexión al resolver las claves del Excel");
      this.setState({ cargando: false });
      return;
    }

    const filas = filasParseadas.map((fila, index) => {
      const resultado = resultados[index];

      if (!resultado || resultado.estado === "NO_ENCONTRADO") {
        return {
          ...fila,
          insumo: null,
          valido: false,
          motivo: "No se encontró un insumo inventariable con esa clave",
        };
      }
      if (resultado.estado === "AMBIGUO") {
        return {
          ...fila,
          insumo: null,
          candidatos: resultado.candidatos || [],
          valido: false,
          motivo: fila.descripcionExcel
            ? `Clave ambigua (${resultado.candidatos?.length || 0}): la Descripción no coincide con ninguno`
            : `Clave ambigua (${resultado.candidatos?.length || 0}): agregue la Descripción`,
        };
      }
      if (!(fila.cantidad > 0)) {
        return {
          ...fila,
          insumo: resultado.insumo,
          valido: false,
          motivo: "Cantidad inválida",
        };
      }
      return {
        ...fila,
        insumo: resultado.insumo,
        desempatadoPorDescripcion: !!resultado.desempatadoPorDescripcion,
        valido: true,
        motivo: null,
      };
    });

    // 2) Disponible en el proyecto de todas las lineas validas, tambien en una
    // sola consulta (no una por renglon).
    const validas = filas.filter((f) => f.valido);
    if (validas.length > 0) {
      try {
        const resInventarios = await axios.post(
          "inventarios/inventarioInsumoProyectoBulk",
          {
            empresaId,
            proyectoId,
            insumos: validas.map((f, index) => ({
              index,
              insumoId: f.insumo._id,
              proyectoId,
              inventariable: f.insumo.inventariable,
              insumoCompuesto: f.insumo.insumoCompuesto,
              noPrincipal: false,
            })),
          },
          { headers: headersToken }
        );
        (resInventarios.data?.results || []).forEach((resItem) => {
          const filaValida = validas[resItem.index];
          if (filaValida) filaValida.cantidadProyecto = resItem.cantidad;
        });
      } catch (error) {
        console.error("Error al consultar inventario en batch:", error);
        message.warning(
          "No se pudo consultar el inventario del proyecto. La columna Disponible queda vacía y habrá que capturarla en cada renglón."
        );
      }
    }

    // 3) Avisos: se cargan igual, pero el usuario tiene que hacer algo despues
    filas.forEach((fila) => {
      if (!fila.valido) return;
      const avisos = [];
      if (fila.insumo.lotesCaducidades) avisos.push("Requiere lotes");
      if (fila.insumo.marbetes) avisos.push("Requiere marbetes");
      const disponible = fila.cantidadProyecto;
      if (typeof disponible === "number" && fila.cantidad > disponible) {
        avisos.push(`Excede disponible (${disponible})`);
      }
      fila.aviso = avisos.length > 0 ? avisos.join(" · ") : null;
    });

    this.setState({ filas, cargando: false });
  };

  confirmarCarga = () => {
    const validas = this.state.filas.filter((f) => f.valido);
    if (validas.length === 0) {
      message.error("No hay líneas válidas para cargar");
      return;
    }
    this.props.onConfirmar(
      validas.map((f) => ({
        insumo: f.insumo,
        cantidad: f.cantidad,
        cantidadProyecto: f.cantidadProyecto,
      }))
    );
  };

  render() {
    const { visible, onCancel } = this.props;
    const { cargando, archivoNombre, filas } = this.state;

    const validas = filas.filter((f) => f.valido).length;
    const invalidas = filas.length - validas;

    const columnas = [
      { title: "Clave", dataIndex: "clave", width: 110 },
      {
        title: "Insumo encontrado",
        render: (_, f) => {
          if (f.insumo) return f.insumo.descripcion;
          // Sin insumo resuelto se muestra lo que traia el Excel, para que se
          // vea contra que se intento empatar.
          if (f.descripcionExcel) return <Text type="secondary">{f.descripcionExcel}</Text>;
          return <Text type="secondary">—</Text>;
        },
      },
      {
        title: "Unidad",
        width: 90,
        render: (_, f) => f.insumo?.unidad || <Text type="secondary">—</Text>,
      },
      { title: "Cantidad", dataIndex: "cantidad", width: 90 },
      {
        title: "Disponible",
        dataIndex: "cantidadProyecto",
        width: 100,
        render: (v) => (typeof v === "number" ? v : <Text type="secondary">—</Text>),
      },
      {
        title: "Estado",
        width: 300,
        render: (_, f) => {
          if (!f.valido) return <Tag color="red">{f.motivo}</Tag>;
          const tagDesempate = f.desempatadoPorDescripcion && (
            <Tag color="blue" title="La clave estaba repetida; se eligió el insumo por la Descripción del Excel">
              Por descripción
            </Tag>
          );
          return (
            <>
              {f.aviso ? <Tag color="orange">{f.aviso}</Tag> : <Tag color="green">Encontrado</Tag>}
              {tagDesempate}
            </>
          );
        },
      },
    ];

    return (
      <ModalItem
        title={
          <span>
            <FileExcelOutlined /> Cargar Insumos desde Excel
          </span>
        }
        visible={visible}
        onCancel={onCancel}
        width={950}
        spinning={cargando}
      >
        <div style={{ padding: "0 15px 15px 15px" }}>
          <Alert
            type="info"
            showIcon
            style={{ marginBottom: 15 }}
            message="El Excel debe tener columnas Clave, Descripción y Cantidad"
            description="La Descripción sólo se usa para desempatar cuando dos insumos comparten la misma clave. Las líneas que se carguen reemplazarán cualquier insumo que ya esté agregado en esta orden. Los insumos con lotes o marbetes se cargan, pero hay que elegirlos a mano en el renglón."
          />

          <ButtonItem
            icon={<DownloadOutlined />}
            onClick={this.descargarPlantilla}
            style={{ marginRight: 10 }}
          >
            Descargar plantilla
          </ButtonItem>

          <Upload showUploadList={false} customRequest={this.onSubirArchivo} accept=".xlsx,.xls">
            <ButtonItem icon={<UploadOutlined />}>Subir Excel</ButtonItem>
          </Upload>

          {archivoNombre && (
            <Text type="secondary" style={{ marginLeft: 10 }}>
              {archivoNombre}
            </Text>
          )}

          {filas.length > 0 && (
            <>
              <Text style={{ display: "block", margin: "15px 0 10px" }}>
                {validas} línea(s) válida(s)
                {invalidas > 0 ? `, ${invalidas} con error` : ""}
              </Text>
              <Table
                size="small"
                rowKey={(f, idx) => idx}
                columns={columnas}
                dataSource={filas}
                pagination={{ pageSize: 8 }}
              />
            </>
          )}

          <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 15, gap: 10 }}>
            <ButtonItem onClick={onCancel} rojo>
              Cancelar
            </ButtonItem>
            <ButtonItem type="primary" disabled={validas === 0} onClick={this.confirmarCarga}>
              Cargar a la Orden ({validas})
            </ButtonItem>
          </div>
        </div>
      </ModalItem>
    );
  }
}

export default ModalCargarExcelProductosAlCosto;
